import React, { useRef, useState, useEffect, useCallback } from 'react'
import fbChart from '../../../assests/fb-chart.png'
import mbChart from '../../../assests/mb-chart.png'
import axiosInstance from '../../../api/axiosInstance'
import { baseurImage } from '../../../Baseurl'
import { useUser } from '../../../Context/UserContext'
import Swal from 'sweetalert2'

const CONDITIONS = [
  { label: 'Acne',    color: '#c0392b' },
  { label: 'Rash',    color: '#d35400' },
  { label: 'Lesion',  color: '#7d3c98' },
  { label: 'Dryness', color: '#b7950b' },
  { label: 'Pigment', color: '#5d4037' },
  { label: 'Other',   color: '#1a5276' },
]

const TOOLS = [
  { id: 'pen',     icon: 'ti ti-pencil', label: 'Pen'    },
  { id: 'dot',     icon: 'ti ti-point',  label: 'Dot'    },
  { id: 'syringe', icon: null,           label: 'Inject' },
  { id: 'eraser',  icon: 'ti ti-eraser', label: 'Eraser' },
]

function drawSyringe(ctx, x, y, color) {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(-Math.PI / 4)

  const c = color || '#1a5276'

  ctx.beginPath()
  ctx.moveTo(0, 14)
  ctx.lineTo(0, 26)
  ctx.strokeStyle = '#888'
  ctx.lineWidth = 1.5
  ctx.lineCap = 'round'
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(-1, 24)
  ctx.lineTo(0, 28)
  ctx.lineTo(1, 24)
  ctx.fillStyle = '#888'
  ctx.fill()

  ctx.beginPath()
  ctx.roundRect(-4, -10, 8, 24, 2)
  ctx.fillStyle = c + '22'
  ctx.fill()
  ctx.strokeStyle = c
  ctx.lineWidth = 1.4
  ctx.stroke()

  for (let i = 0; i < 4; i++) {
    const ly = -4 + i * 4
    ctx.beginPath()
    ctx.moveTo(-4, ly)
    ctx.lineTo(-2, ly)
    ctx.strokeStyle = c
    ctx.lineWidth = 0.8
    ctx.stroke()
  }

  ctx.beginPath()
  ctx.moveTo(0, -10)
  ctx.lineTo(0, -18)
  ctx.strokeStyle = c
  ctx.lineWidth = 1.5
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(-6, -18)
  ctx.lineTo(6, -18)
  ctx.strokeStyle = c
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.stroke()

  ctx.beginPath()
  ctx.rect(-3.5, -11, 7, 3)
  ctx.fillStyle = c + '88'
  ctx.fill()

  ctx.restore()
}

const BChart = ({ patientId, patientDetails }) => {
  const { user } = useUser()
  const canvasRef  = useRef(null)
  const imgRef     = useRef(null)
  const isDrawing  = useRef(false)
  const lastPos    = useRef(null)
  const history    = useRef([])

  const isMale    = (patientDetails?.gender || '').toString().toLowerCase() === 'male'
  const bodyChart = isMale ? mbChart : fbChart

  const [tool,            setTool]            = useState('pen')
  const [condition,       setCondition]       = useState(CONDITIONS[0])
  const [brushSize,       setBrushSize]       = useState(4)
  const [notes,           setNotes]           = useState('')
  const [saving,          setSaving]          = useState(false)
  const [savedCharts,     setSavedCharts]     = useState([])
  const [loadingCharts,   setLoadingCharts]   = useState(false)
  const [activeChart,     setActiveChart]     = useState(null)
  const [canvasSize,      setCanvasSize]      = useState({ width: 800, height: 1200 })
  const [suggestions,     setSuggestions]     = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const notesSaveTimer    = useRef(null)
  const suggestionTimer   = useRef(null)

  // Match canvas internal resolution to natural image size for accurate hit-testing
  const onImgLoad = (e) => {
    setCanvasSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })
  }

  const fetchCharts = useCallback(async () => {
    if (!patientId) return
    setLoadingCharts(true)
    try {
      const res = await axiosInstance.get(`getDermaCharts/${patientId}?chart_type=body`)
      if (res.data?.success) setSavedCharts(res.data.data || [])
    } catch {
      // silently fail — endpoint may not exist yet
    } finally {
      setLoadingCharts(false)
    }
  }, [patientId])

  useEffect(() => { fetchCharts() }, [fetchCharts])

  const handleNotesChange = (e) => {
    const value = e.target.value
    setNotes(value)

    // Fetch suggestions with debounce (300 ms)
    clearTimeout(suggestionTimer.current)
    if (value.trim().length >= 2) {
      suggestionTimer.current = setTimeout(async () => {
        try {
          const res = await axiosInstance.get(`getDermaNotesSuggestions?q=${encodeURIComponent(value.trim())}`)
          if (res.data?.success) {
            const filtered = (res.data.data || []).filter(s => s !== value)
            setSuggestions(filtered)
            setShowSuggestions(filtered.length > 0)
          }
        } catch { /* ignore */ }
      }, 300)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }

    // Auto-save note to history with debounce (1.5 s)
    clearTimeout(notesSaveTimer.current)
    if (value.trim().length >= 3) {
      notesSaveTimer.current = setTimeout(async () => {
        try {
          await axiosInstance.post('saveDermaNote', { note_text: value.trim() })
        } catch { /* ignore */ }
      }, 1500)
    }
  }

  const applySuggestion = (text) => {
    setNotes(text)
    setSuggestions([])
    setShowSuggestions(false)
  }

  const getPos = (e) => {
    const canvas = canvasRef.current
    const rect   = canvas.getBoundingClientRect()
    const scaleX = canvas.width  / rect.width
    const scaleY = canvas.height / rect.height
    const src    = e.touches ? e.touches[0] : e
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    }
  }

  const saveSnap = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    history.current.push(canvas.toDataURL())
    if (history.current.length > 40) history.current.shift()
  }

  const startDraw = (e) => {
    e.preventDefault()
    saveSnap()
    isDrawing.current = true
    const pos = getPos(e)
    lastPos.current = pos
    if (tool === 'dot') {
      const ctx = canvasRef.current.getContext('2d')
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, brushSize * 2, 0, Math.PI * 2)
      ctx.fillStyle = condition.color + 'cc'
      ctx.fill()
      isDrawing.current = false
    }
    if (tool === 'syringe') {
      drawSyringe(canvasRef.current.getContext('2d'), pos.x, pos.y, condition.color)
      isDrawing.current = false
    }
  }

  const draw = (e) => {
    if (!isDrawing.current) return
    e.preventDefault()
    const pos = getPos(e)
    const ctx = canvasRef.current.getContext('2d')
    ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over'
    ctx.lineWidth   = tool === 'eraser' ? brushSize * 4 : brushSize
    ctx.lineCap     = 'round'
    ctx.lineJoin    = 'round'
    ctx.strokeStyle = condition.color + 'cc'
    ctx.beginPath()
    ctx.moveTo(lastPos.current.x, lastPos.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastPos.current = pos
  }

  const stopDraw = () => {
    isDrawing.current = false
    if (canvasRef.current)
      canvasRef.current.getContext('2d').globalCompositeOperation = 'source-over'
  }

  const undo = () => {
    const canvas = canvasRef.current
    if (!canvas || history.current.length === 0) return
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.onload = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0) }
    img.src = history.current.pop()
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    saveSnap()
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
  }

  const saveChart = async () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const imageData = canvas.toDataURL('image/png')
    setSaving(true)
    try {
      const res = await axiosInstance.post('saveDermaChart', {
        patient_id: patientId,
        chart_type: 'body',
        image_data: imageData,
        notes,
        created_by: user?.id || null,
      })
      if (res.data?.success) {
        setActiveChart(res.data.data?.id || null)
        Swal.fire({ icon: 'success', title: 'Saved', text: 'Body chart saved successfully.', timer: 1500, showConfirmButton: false })
        fetchCharts()
      } else {
        Swal.fire('Error', res.data?.message || 'Failed to save chart', 'error')
      }
    } catch {
      Swal.fire('Error', 'Failed to save chart', 'error')
    } finally {
      setSaving(false)
    }
  }

  const loadChart = (chart) => {
    const canvas = canvasRef.current
    if (!canvas) return
    saveSnap()
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
    }
    img.src = baseurImage + chart.image_path
    setNotes(chart.notes || '')
    setActiveChart(chart.id)
  }

  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

        {/* ── Chart area (3/4) ─────────────────────────────────────────────── */}
        <div style={{ flex: 3, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          {/* 70% width → image aspect ratio naturally gives 30% less height, no clipping, no scroll */}
          <div style={{ position: 'relative', width: '70%', borderRadius: 10, overflow: 'hidden',
            border: '1px solid #e0e0e0', background: '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <img
              ref={imgRef}
              src={bodyChart}
              alt="Body Chart"
              draggable={false}
              onLoad={onImgLoad}
              style={{ width: '100%', display: 'block', userSelect: 'none', pointerEvents: 'none' }}
            />
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                cursor: tool === 'eraser' ? 'cell' : 'crosshair', touchAction: 'none' }}
              onMouseDown={startDraw} onMouseMove={draw}
              onMouseUp={stopDraw}   onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
          </div>
        </div>

        {/* ── Tools panel (1/4) ────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Tool */}
          <div>
            <div style={{ fontSize: '0.8em', fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Tool</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {TOOLS.map(t => (
                <button key={t.id} title={t.label} onClick={() => setTool(t.id)}
                  style={{ flex: 1, padding: '6px 2px', border: `1.5px solid ${tool === t.id ? '#222' : '#e0e0e0'}`,
                    borderRadius: 7, background: tool === t.id ? '#222' : '#fff',
                    color: tool === t.id ? '#fff' : '#555', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  {t.icon
                    ? <i className={t.icon} style={{ fontSize: 15 }} />
                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 2l4 4" /><path d="M14 6l4 4" />
                        <path d="M5 15l-3 3v3h3l3-3" /><path d="M9 11l4 4" />
                        <path d="M10.5 9.5l4 4" /><line x1="22" y1="2" x2="8" y2="16" />
                      </svg>
                  }
                  <span style={{ fontSize: '0.72em' }}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Brush size */}
          <div>
            <div style={{ fontSize: '0.8em', fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Size — {brushSize}px
            </div>
            <input type="range" min={1} max={16} value={brushSize}
              onChange={e => setBrushSize(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#222' }} />
          </div>

          {/* Condition */}
          <div>
            <div style={{ fontSize: '0.8em', fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Condition</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {CONDITIONS.map(c => (
                <button key={c.label} onClick={() => { setCondition(c); if (tool === 'eraser') setTool('pen') }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                    border: `1.5px solid ${condition.label === c.label ? c.color : '#e0e0e0'}`,
                    borderRadius: 7, background: condition.label === c.label ? c.color + '14' : '#fff',
                    cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ width: 11, height: 11, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                  <span style={{ fontWeight: condition.label === c.label ? 700 : 400,
                    color: condition.label === c.label ? c.color : '#444' }}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <div style={{ fontSize: '0.8em', fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Notes</div>
            <div style={{ position: 'relative' }}>
              <textarea rows={4} placeholder="Add clinical notes..."
                value={notes} onChange={handleNotesChange}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                style={{ width: '100%', fontSize: 'inherit', border: '1.5px solid #e0e0e0',
                  borderRadius: 7, padding: '6px 8px', resize: 'vertical',
                  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              {showSuggestions && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                  background: '#fff', border: '1.5px solid #d0d7e2', borderRadius: 7,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.10)', maxHeight: 200, overflowY: 'auto',
                }}>
                  {suggestions.map((s, i) => (
                    <div key={i}
                      onMouseDown={() => applySuggestion(s)}
                      style={{
                        padding: '9px 12px', cursor: 'pointer', fontSize: '0.95em',
                        borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        color: '#333',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f4f7ff'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      <i className="ti ti-history" style={{ marginRight: 5, color: '#2CA87F', fontSize: '0.9em' }} />
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={undo}
              style={{ flex: 1, padding: '6px 0', border: '1px solid #e0e0e0', borderRadius: 7,
                background: '#f8f9fb', color: '#555', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <i className="ti ti-arrow-back-up" /> Undo
            </button>
            <button onClick={clearCanvas}
              style={{ flex: 1, padding: '6px 0', border: '1px solid #fcc', borderRadius: 7,
                background: '#fff5f5', color: '#c0392b', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <i className="ti ti-trash" /> Clear
            </button>
          </div>

          {/* Save */}
          <button onClick={saveChart} disabled={saving}
            style={{ padding: '8px 0', border: 'none', borderRadius: 7,
              background: saving ? '#b2c5f8' : '#4680ff', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontWeight: 600 }}>
            {saving
              ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
              : <><i className="ti ti-device-floppy" /> Save Chart</>}
          </button>

        </div>
      </div>

      {/* ── Saved Charts Gallery ─────────────────────────────────────────────── */}
      {(loadingCharts || savedCharts.length > 0) && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.8em', fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Saved Charts
          </div>
          {loadingCharts ? (
            <div style={{ color: '#aaa', fontSize: '0.85em' }}>Loading…</div>
          ) : (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {savedCharts.map(chart => {
                const isActive = chart.id === activeChart
                const dateStr  = new Date(chart.created_at).toLocaleDateString('en-GB')
                const timeStr  = new Date(chart.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })
                return (
                  <div key={chart.id}
                    onClick={() => loadChart(chart)}
                    style={{
                      cursor: 'pointer', borderRadius: 8, padding: '8px 12px',
                      border: `2px solid ${isActive ? '#2CA87F' : '#e0e0e0'}`,
                      boxShadow: isActive ? '0 0 0 3px #2CA87F33' : '0 1px 4px rgba(0,0,0,0.07)',
                      background: isActive ? '#edfaf4' : '#fff',
                      transition: 'all 0.15s', minWidth: 140, maxWidth: 200,
                    }}>
                    <div style={{ fontSize: '0.78em', fontWeight: 700, color: '#1a202c' }}>
                      {dateStr} <span style={{ fontWeight: 400, color: '#888' }}>{timeStr}</span>
                    </div>
                    <div style={{ fontSize: '0.72em', color: '#2CA87F', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <i className="ti ti-user" style={{ marginRight: 3 }} />{chart.created_by_name || 'Unknown'}
                    </div>
                    {chart.notes && (
                      <div style={{ fontSize: '0.7em', color: '#888', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {chart.notes}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default BChart
